import { memo, useEffect, useRef } from 'react'
import { parseHighlights } from './Subheading'
import { playSound } from '../../utils/soundFX'

const AUTO_CLOSE_MS = 30_000

/**
 * SatelliteInfoPanel
 *
 * Info panel shown when the user clicks a satellite, gateway, or location hotspot.
 * Reuses the did-you-know visual language (dark background, red left-border
 * accent, Public Sans typography) — no new CSS required.
 *
 * Auto-dismisses after 30 seconds of inactivity. Timer resets each time
 * the panel becomes visible (i.e. a new hotspot is opened).
 *
 * Props:
 *   model       — object with .infoPanelName / .panelName / .name and .info fields
 *   visible     — show/hide without unmounting; when false returns null
 *   offset      — { x, y } pixel translate applied to the panel
 *   onClose     — called when the panel closes (button or auto-dismiss)
 *   onCta       — called when the CTA button is clicked (optional)
 *   ctaLabel    — label for the CTA button (optional, e.g. 'PLAY NOW')
 *   position    — dyk-pos variant, e.g. 'bottom-left' (default) or 'top-left'
 *   autoCloseMs — auto-dismiss delay in ms (default 30 000)
 *   thumbnail   — optional image src shown to the left of the text box
 */
export const SatelliteInfoPanel = memo(function SatelliteInfoPanel({ model, visible, offset, onClose, onCta, ctaLabel, position = 'bottom-left', autoCloseMs = AUTO_CLOSE_MS, thumbnail }) {
  const timerRef = useRef(null)

  useEffect(() => {
    if (visible) {
      if (timerRef.current) clearTimeout(timerRef.current)
      timerRef.current = setTimeout(() => {
        playSound('DataPoint_Close')
        onClose?.()
      }, autoCloseMs)
    }
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current)
    }
  }, [visible, model])

  if (!visible || !model) return null

  const info = model.info ?? ''
  const paragraphs = Array.isArray(info) ? info : [info]

  const x = offset?.x ?? 0
  const y = offset?.y ?? 0
  const offsetStyle = (x !== 0 || y !== 0) ? { '--dyk-x-offset': `${x}px`, '--dyk-y-offset': `${y}px` } : undefined

  const handleClose = () => {
    playSound('DataPoint_Close')
    onClose?.()
  }

  const handleCta = () => {
    playSound('PlotCourse')
    onCta?.()
  }

  return (
    <div className={`did-you-know-panel dyk-pos--${position}`} style={offsetStyle}>
      <div className="dyk-panel-row">
      {thumbnail && (
        <img src={thumbnail} className="dyk-thumbnail" alt="" />
      )}
      <div className="did-you-know-window">
        <div className="dyk-heading-row">
          <p className="dyk-heading">{parseHighlights(model.infoPanelName ?? model.panelName ?? model.name ?? '')}</p>
          {onClose && (
            <button className="dyk-close" onClick={handleClose} aria-label="Close">✕</button>
          )}
        </div>
        {paragraphs.map((text, i) => (
          <p key={i} className="dyk-body">{parseHighlights(text)}</p>
        ))}
        {onCta && ctaLabel && (
          <div className="dyk-cta-row">
            <button className="location-panel-cta" onClick={handleCta}>
              <span>{ctaLabel}</span>
              <span className="rect_button-chevrons">&gt;&gt;&gt;</span>
            </button>
          </div>
        )}
      </div>
      </div>
    </div>
  )
})
